const HYDRATION_CACHE = {}
const HYDRATION_SMALL_CACHE = {}
const HYDRATION_MEDIUM_CACHE = {}
const HYDRATION_QUIZ_CACHE = {}
const SKELETON_ATTRUBUTES = [
  'data-product-href',
  'data-product-image-primary',
  'data-product-image-secondary',
  'data-product-title',
  'data-product-description',
  'data-product-price',
]
class ProductCard extends HHBaseElement {
  constructor() {
    super()
    this._hydrated = !this.shouldHydrate
    this._selectItems = this._selectItems.bind(this)
    this._handleInCartToggle = this._handleInCartToggle.bind(this)
    this._trackQuickShopToggle = this._trackQuickShopToggle.bind(this)
    this._handleVariantChange = this._handleVariantChange.bind(this)
    this.variantSelected = false
    this._skeletonTemplate = document.getElementById('skeleton-product-card')
  }

  get productHandle() {
    return this.getAttribute('product-handle')
  }

  set productHandle(value) {
    this.setAttribute('product-handle', value)
  }

  get sectionHeadline() {
    return this.getAttribute('data-section-headline')
  }

  get cardSize() {
    return this.getAttribute('card-size') ?? ''
  }

  get shouldHydrate() {
    return this.hasAttribute('hydrate')
  }

  get variantId() {
    return this.getAttribute('data-id') || ''
  }

  set variantId(value) {
    this.setAttribute('data-id', value)
  }

  get productUrl() {
    return this.getAttribute('data-product-href') ?? ''
  }

  get wearItWithNosto() {
    return this.hasAttribute('wear-it-with-nosto')
  }

  static get observedAttributes() {
    return ['product-handle']
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === 'product-handle' &&
      newValue !== oldValue &&
      newValue &&
      this.shouldHydrate
    ) {
      this._hydrated = false
      this._hydrate()
    }
  }

  connectedCallback() {
    if (this.shouldHydrate) {
      return this._hydrate()
    }
    this._selectItems()
    this._attachListeners()
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  async _hydrate(bypassCache = false) {
    const cacheVar =
      this.cardSize === 'small'
        ? HYDRATION_SMALL_CACHE
        : this.cardSize === 'medium'
          ? HYDRATION_MEDIUM_CACHE
          : this.cardSize === 'quiz'
            ? HYDRATION_QUIZ_CACHE
            : HYDRATION_CACHE
    const viewParam = this.cardSize
      ? `product-card-${this.cardSize}`
      : 'product-card'

    if (cacheVar[this.productHandle] && !bypassCache) {
      this.innerHTML = cacheVar[this.productHandle]
    } else {
      if (!this.cardSize) this._renderSkeleton()

      let url = `${__GLOBAL__.routes.rootUrl}/products/${this.productHandle}?view=${viewParam}`
      if (this.variantId) {
        url += `&variant=${this.variantId}`
      }
      const request = await fetch(url)
      if (!request.ok) return this.remove() // May return 404 if product is unavailable
      const container = document.createElement('div')
      container.innerHTML = await request.text()
      const productCardNode = container.querySelector(
        viewParam.replace('-quiz', '')
      )
      if (!productCardNode) return this.remove() // May return empty div if variant is unavailable
      cacheVar[this.productHandle] = productCardNode.innerHTML
      this.innerHTML = cacheVar[this.productHandle]
    }
    this._hydrated = true
    this._handleHydration()
  }

  _renderSkeleton() {
    if (!this._skeletonTemplate) return null
    const contents = this._skeletonTemplate.cloneNode(true)
    this.innerHTML = contents.innerHTML
    SKELETON_ATTRUBUTES.forEach((attr) => {
      const data = this.getAttribute(attr)
      if (!!data) {
        const targets = Array.from(this.querySelectorAll(`[${attr}]`))
        targets.forEach((target) => {
          if (target.tagName === 'ADAPTIVE-IMAGE') {
            target.setAttribute('data-image', data)
          } else if (target.tagName === 'A') {
            target.setAttribute('href', data)
          } else {
            target.innerHTML = data
          }
        })
      }
    })
  }

  _handleHydration() {
    this._selectItems()
    this._removeListeners()
    this._attachListeners()
    this.classList.add('hydrated')
    const wrapperAnchor = this.querySelector('.js-product-card-link')
    const titleSpan = this.querySelector('.js-product-card-title')

    if (wrapperAnchor) {
      wrapperAnchor.setAttribute(
        'aria-labelledby',
        this.productHandle + '--' + this.sectionHeadline
      )
      titleSpan?.setAttribute(
        'id',
        this.productHandle + '--' + this.sectionHeadline
      )
    }
    this.setAttribute('role', 'group')
    this.setAttribute('aria-label', 'product')
    this._setURLPostfix()
    this._handleSelectShadeText()
  }

  _handleSelectShadeText() {
    if (!this.wearItWithNosto) {
      return
    }

    const selectShadeText = this.querySelector('[data-select-your-shade-text')
    const selectedSwatch = this.querySelector('[data-selected-swatch]')
    const selectedSwatchText = this.querySelector('[data-selected-swatch-text]')
    const addToCartButton = this.querySelector('button[data-variant-id]')
    if (!selectShadeText || !selectedSwatch || !selectedSwatchText) {
      return
    }

    if (this.variantSelected || addToCartButton?.dataset?.variantId !== '') {
      selectShadeText.classList.add('tw-hidden')
      selectedSwatch.classList.remove('tw-hidden')
      selectedSwatchText.classList.remove('tw-hidden')
      return
    }

    selectShadeText.classList.remove('tw-hidden')
    selectedSwatch.classList.add('tw-hidden')
    selectedSwatchText.classList.add('tw-hidden')
  }

  applyUrlPostfix(url) {
    try {
      const urlPostfix = this.getAttribute('url-postfix')
      if (!!urlPostfix) {
        const href = new URL(url)
        href.searchParams.append(...urlPostfix.split('='))
        return href.toString()
      }
      return url
    } catch (error) {
      console.error(error)
      return url
    }
  }

  _setURLPostfix() {
    const urlPostfix = this.getAttribute('url-postfix')

    if (!!urlPostfix) {
      try {
        const links = this.querySelectorAll('.js-product-card-link')
        for (const link of links) {
          if (link && !!link.href) {
            link.href = this.applyUrlPostfix(link.href)
          }
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  _selectItems() {
    this.quickShopToggle = this.querySelector('.js-toggle-quick-shop')
    this.inCartToggle = this.querySelector('.js-in-cart-toggle')
  }

  _handleCardClick(e) {
    if (!this.productUrl || e.target.contains(this.quickShopToggle)) {
      return
    }

    window.location.href = this.applyUrlPostfix(this.productUrl)
  }

  _attachListeners() {
    this.addEventListener('click', this._handleCardClick)

    if (this.quickShopToggle) {
      this.quickShopToggle.addEventListener('click', this._trackQuickShopToggle)
    }

    if (this.inCartToggle) {
      this.inCartToggle.addEventListener('click', this._handleInCartToggle)
    }

    this.addEventListener('change', this._handleVariantChange)
  }

  _trackQuickShopToggle() {
    this.triggerEvent('click::quick-shop-toggle', {
      product_id: this.getAttribute('data-id'),
      variant_id: this.quickShopToggle.dataset.value,
    })
  }

  _removeListeners() {
    this.removeEventListener('click', this._handleCardClick)

    if (this.quickShopToggle) {
      this.quickShopToggle.removeEventListener(
        'click',
        this._trackQuickShopToggle
      )
    }

    this.removeEventListener('change', this._handleVariantChange)
  }

  _handleVariantChange(event) {
    const variantId = event.target.dataset.variantId
    const productHandle = event.target.dataset.productHandle

    if (variantId) {
      if (productHandle && productHandle !== this.productHandle) {
        this.productHandle = productHandle
      }
      this.variantId = variantId
      this._hydrate(true)
      this.variantSelected = true
    }
  }

  async _handleInCartToggle(e) {
    e.preventDefault()
    const cartLineItemKey = e.currentTarget.dataset.lineItemKey
    const cartItemsToRemove = (e.currentTarget.dataset.itemsToRemove || '')
      .split(',')
      .filter(Boolean)
    const property = this.inCartToggle.dataset.lineProperty
    if (cartItemsToRemove.length) {
      await window.__CART__CONTROLLER__.update(
        cartItemsToRemove.reduce((acc, item) => {
          acc[item] = 0
          return acc
        }, {})
      )
    }
    if (cartLineItemKey) {
      await window.__CART__CONTROLLER__.update({
        [cartLineItemKey]: 0,
      })
    } else {
      await window.__CART__CONTROLLER__.add([
        {
          id: this.inCartToggle.dataset.value,
          quantity: 1,
          ...(property ? { properties: { [property]: true } } : {}),
        },
      ])

      this.triggerEvent('click::add-to-cart', {
        product_id: this.dataset.id,
        variant_id: this.inCartToggle.dataset.value,
      })
    }

    this.triggerGlobalEvent('main-cart::refresh')
  }
}
window.customElements.define('product-card', ProductCard)
